import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableFooter,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { useState, useMemo } from "react";

import SiteIcons from "@/components/SiteIcons/SiteIcons";

const headCells = [
  {
    id: "name",
    numeric: false,
    label: "Local",
    align: "left",
  },
  {
    id: "IEBTimestamp",
    numeric: false,
    label: "IEB Date",
    align: "right",
  },
];

const SortableTableHead = ({ order, orderBy, onRequestSort }) => {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

function IEBTable({ filteredRows, handleOpenModal }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    // if sorting by the date column, turn the strings into real numeric timestamps
    if (orderBy === "IEBTimestamp") {
      valueA = valueA ? Date.parse(valueA) : 0;
      valueB = valueB ? Date.parse(valueB) : 0;
    }

    if (valueB < valueA) {
      return -1;
    }
    if (valueB > valueA) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const visibleRows = useMemo(
    () =>
      [...filteredRows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    // getComparator is recreated each render; depending on it would
    // invalidate the memo on every render and defeat its purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {visibleRows.length ? (
        <TableContainer
          component={Paper}
          elevation={0}
          data-testid="IEB-files-table"
        >
          <Table
            sx={{
              // vertical lines for all cells
              "& .MuiTableCell-root": {
                borderLeft: "1px solid rgba(224, 224, 224, 1)",
              },
              // close the grid on the far right
              "& .MuiTableCell-root:last-child": {
                borderRight: "1px solid rgba(224, 224, 224, 1)",
              },
              // target cells specifically inside the last row
              "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                borderLeft: "1px solid rgba(224, 224, 224, 1)",
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              },
            }}
            aria-label="simple table"
          >
            <SortableTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />

            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      cursor: "pointer",
                      fontWeight: "medium",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        boxShadow: "inset 0 0 0 2px #bbd8f1",
                      },
                    }}
                    align="right"
                    onClick={() => handleOpenModal(row)}
                  >
                    {row.IEBTimestamp}
                  </TableCell>
                </TableRow>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={2} />{" "}
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  colSpan={3}
                  count={filteredRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  slotProps={{
                    select: {
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      native: true,
                    },
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "4px",
          }}
        >
          <SiteIcons iconstring="circular-loader" page="sitewide-loader" />
        </Box>
      )}
    </Box>
  );
}

export default IEBTable;
